// Game Voting Service - Players vote on their top 3 games per category before schedule is set
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

// Save or update a player's votes for a competition (can save drafts before submitting)
// votes format: { categoryId: [gameSectionId1, gameSectionId2, gameSectionId3] } (ordered by preference)
export const savePlayerVotes = async (competitionId, playerId, playerName, votes, submitted = false) => {
  const voteId = `${competitionId}_${playerId}`;
  const voteRef = doc(db, 'gameVotes', voteId);

  const existingVote = await getDoc(voteRef);

  const voteData = {
    competitionId,
    playerId,
    playerName,
    votes, // { categoryId: [top3 gameSectionIds in order] }
    submitted, // true when player finalizes their votes
    updatedAt: serverTimestamp()
  };

  if (!existingVote.exists()) {
    voteData.createdAt = serverTimestamp();
  }

  await setDoc(voteRef, voteData, { merge: true });

  return { id: voteId, ...voteData };
};

// Submit player's votes (finalize them)
export const submitPlayerVotes = async (competitionId, playerId) => {
  const voteId = `${competitionId}_${playerId}`;
  const voteRef = doc(db, 'gameVotes', voteId);

  await updateDoc(voteRef, {
    submitted: true,
    submittedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// Get a player's votes for a competition
export const getPlayerVotes = async (competitionId, playerId) => {
  const voteId = `${competitionId}_${playerId}`;
  const voteRef = doc(db, 'gameVotes', voteId);
  const voteSnap = await getDoc(voteRef);

  if (!voteSnap.exists()) {
    return null;
  }

  return {
    id: voteSnap.id,
    ...voteSnap.data()
  };
};

// Get all votes for a competition
export const getAllVotesForCompetition = async (competitionId) => {
  const votesRef = collection(db, 'gameVotes');
  const q = query(votesRef, where('competitionId', '==', competitionId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get all submitted votes for a competition
export const getSubmittedVotes = async (competitionId) => {
  const votesRef = collection(db, 'gameVotes');
  const q = query(
    votesRef,
    where('competitionId', '==', competitionId),
    where('submitted', '==', true)
  );
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Check if all players in a competition have submitted their votes
export const checkAllPlayersVoted = async (competitionId, playerIds) => {
  const submittedVotes = await getSubmittedVotes(competitionId);
  const submittedPlayerIds = new Set(submittedVotes.map(v => v.playerId));

  const allVoted = playerIds.every(id => submittedPlayerIds.has(id));
  const votedCount = playerIds.filter(id => submittedPlayerIds.has(id)).length;

  return {
    allVoted,
    votedCount,
    totalPlayers: playerIds.length,
    submittedPlayerIds: Array.from(submittedPlayerIds),
    pendingPlayerIds: playerIds.filter(id => !submittedPlayerIds.has(id))
  };
};

// Calculate aggregated results for a competition
// Returns games ranked by weighted votes (1st = 3 points, 2nd = 2 points, 3rd = 1 point)
export const calculateVotingResults = async (competitionId) => {
  const submittedVotes = await getSubmittedVotes(competitionId);

  if (submittedVotes.length === 0) {
    return {};
  }

  // Aggregate scores by category
  const resultsByCategory = {};

  for (const vote of submittedVotes) {
    const votes = vote.votes || {};

    for (const [categoryId, gameSectionIds] of Object.entries(votes)) {
      if (!resultsByCategory[categoryId]) {
        resultsByCategory[categoryId] = {};
      }

      // Assign points: 1st = 3, 2nd = 2, 3rd = 1
      gameSectionIds.forEach((gameSectionId, index) => {
        const points = 3 - index; // 3, 2, 1
        if (!resultsByCategory[categoryId][gameSectionId]) {
          resultsByCategory[categoryId][gameSectionId] = {
            gameSectionId,
            points: 0,
            firstPlaceVotes: 0,
            secondPlaceVotes: 0,
            thirdPlaceVotes: 0
          };
        }
        resultsByCategory[categoryId][gameSectionId].points += points;

        if (index === 0) resultsByCategory[categoryId][gameSectionId].firstPlaceVotes++;
        else if (index === 1) resultsByCategory[categoryId][gameSectionId].secondPlaceVotes++;
        else if (index === 2) resultsByCategory[categoryId][gameSectionId].thirdPlaceVotes++;
      });
    }
  }

  // Sort games within each category by points (descending)
  const sortedResults = {};
  for (const [categoryId, games] of Object.entries(resultsByCategory)) {
    sortedResults[categoryId] = Object.values(games).sort((a, b) => {
      // Sort by points, then by first place votes as tiebreaker
      if (b.points !== a.points) return b.points - a.points;
      if (b.firstPlaceVotes !== a.firstPlaceVotes) return b.firstPlaceVotes - a.firstPlaceVotes;
      return b.secondPlaceVotes - a.secondPlaceVotes;
    });
  }

  return sortedResults;
};

// Get voting results with game details
export const getVotingResultsWithGameDetails = async (competitionId, gameSections, categories) => {
  const resultsByCategory = await calculateVotingResults(competitionId);

  const resultsWithDetails = {};

  for (const [categoryId, games] of Object.entries(resultsByCategory)) {
    const category = categories.find(c => c.id === categoryId);

    resultsWithDetails[categoryId] = {
      categoryId,
      categoryName: category?.name || 'Unknown Category',
      categoryIcon: category?.icon || '',
      games: games.map(gameData => {
        const gameSection = gameSections.find(gs => gs.id === gameData.gameSectionId);
        return {
          ...gameData,
          gameName: gameSection?.name || 'Unknown Game',
          photoUrl: gameSection?.photoUrl || null,
          averageDurationMinutes: gameSection?.averageDurationMinutes || null,
          rulesUrl: gameSection?.rulesUrl || null
        };
      })
    };
  }

  return resultsWithDetails;
};

// Get voting status for a competition
export const getVotingStatus = async (competitionId) => {
  const competitionRef = doc(db, 'competitions', competitionId);
  const competitionSnap = await getDoc(competitionRef);

  if (!competitionSnap.exists()) {
    return null;
  }

  const data = competitionSnap.data();
  return {
    votingOpen: data.votingOpen !== false, // Default to open if not set
    votingClosedAt: data.votingClosedAt || null,
    scheduleConfirmed: data.scheduleConfirmed ?? false
  };
};

// Close voting for a competition (organizer action)
export const closeVoting = async (competitionId) => {
  const competitionRef = doc(db, 'competitions', competitionId);
  await updateDoc(competitionRef, {
    votingOpen: false,
    votingClosedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// Confirm schedule (removes voting section from dashboard)
export const confirmSchedule = async (competitionId) => {
  const competitionRef = doc(db, 'competitions', competitionId);
  await updateDoc(competitionRef, {
    scheduleConfirmed: true,
    scheduleConfirmedAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  });
};

// Reopen voting (if organizer changes their mind)
export const reopenVoting = async (competitionId) => {
  const competitionRef = doc(db, 'competitions', competitionId);
  await updateDoc(competitionRef, {
    votingOpen: true,
    votingClosedAt: null,
    scheduleConfirmed: false,
    updatedAt: serverTimestamp()
  });
};

// Delete all votes for a competition (used when resetting)
export const deleteAllVotesForCompetition = async (competitionId) => {
  const votes = await getAllVotesForCompetition(competitionId);

  const deletePromises = votes.map(vote => {
    const voteRef = doc(db, 'gameVotes', vote.id);
    return deleteDoc(voteRef);
  });

  await Promise.all(deletePromises);
};

// Delete a specific player's vote
export const deletePlayerVote = async (competitionId, playerId) => {
  const voteId = `${competitionId}_${playerId}`;
  const voteRef = doc(db, 'gameVotes', voteId);
  await deleteDoc(voteRef);
};
